<template>
  <div ref="grid" class="bracket-skeleton" aria-hidden="true">
    <svg
      v-if="connectorPaths.length"
      class="skeleton-connectors"
      :width="connectorBox.w"
      :height="connectorBox.h"
    >
      <path
        v-for="conn in connectorPaths"
        :key="conn.id"
        :d="conn.d"
        stroke="#e0e0e0"
        stroke-width="1.5"
        fill="none"
        stroke-dasharray="4 4"
      />
    </svg>

    <div v-for="col in columnCount" :key="col" class="skeleton-column">
      <div class="skeleton-header"></div>
      <div
        v-for="row in matchesPerColumn(col - 1)"
        :key="`${col}-${row}`"
        :ref="`card-${col - 1}-${row - 1}`"
        class="skeleton-card"
        :style="cardSlotStyle(col - 1, row - 1)"
      ></div>
    </div>
  </div>
</template>

<script>
// Standalone loading state that mimics the bracket silhouette while data
// resolves. Used by ChampionshipOverview when overview/bracket are still
// fetching. Pure CSS shimmer — no images, no JS animation loop.
export default {
  name: 'ChampionshipBracketSkeleton',
  props: {
    columnCount: { type: Number, default: 4 },
  },
  data() {
    return {
      connectorPaths: [],
      connectorBox: { w: 0, h: 0 },
      _raf: null,
      _resizeObserver: null,
    };
  },
  mounted() {
    this.scheduleRecompute();
    if (typeof ResizeObserver !== 'undefined' && this.$refs.grid) {
      this._resizeObserver = new ResizeObserver(() => this.scheduleRecompute());
      this._resizeObserver.observe(this.$refs.grid);
    }
    window.addEventListener('resize', this.scheduleRecompute);
  },
  beforeDestroy() {
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._resizeObserver) this._resizeObserver.disconnect();
    window.removeEventListener('resize', this.scheduleRecompute);
  },
  watch: {
    columnCount() { this.scheduleRecompute(); },
  },
  methods: {
    matchesPerColumn(idx) {
      // 8 → 4 → 2 → 1 silhouette by default; clamps for any column count.
      const max = Math.pow(2, this.columnCount - 1 - idx);
      return Math.max(1, max);
    },
    cardSlotStyle(columnIndex, rowIndex) {
      // Mirror the spacing used by the real bracket so the connector geometry
      // lines up the same way: each subsequent column has cards at doubled
      // vertical stride, with an offset so pairs centre on the previous round.
      const rowSize = 108; // approx height + margin
      const stride = rowSize * Math.pow(2, columnIndex);
      const offset = (Math.pow(2, columnIndex) - 1) * (rowSize / 2);
      return {
        marginTop: rowIndex === 0 ? `${offset}px` : `${stride - rowSize}px`,
      };
    },
    scheduleRecompute() {
      this.$nextTick(() => {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => {
          requestAnimationFrame(() => this.compute());
        });
      });
    },
    compute() {
      const grid = this.$refs.grid;
      if (!grid) return;
      const gridRect = grid.getBoundingClientRect();
      const paths = [];
      // Each card at (col=c, row=r) connects to card at (col=c+1, row=floor(r/2))
      // in the standard single-elimination silhouette.
      for (let c = 0; c < this.columnCount - 1; c++) {
        const sourceCount = this.matchesPerColumn(c);
        for (let r = 0; r < sourceCount; r++) {
          const sourceRefs = this.$refs[`card-${c}-${r}`];
          const targetRefs = this.$refs[`card-${c + 1}-${Math.floor(r / 2)}`];
          const sourceEl = Array.isArray(sourceRefs) ? sourceRefs[0] : sourceRefs;
          const targetEl = Array.isArray(targetRefs) ? targetRefs[0] : targetRefs;
          if (!sourceEl || !targetEl) continue;
          const s = sourceEl.getBoundingClientRect();
          const t = targetEl.getBoundingClientRect();
          const sx = s.right - gridRect.left;
          const sy = s.top + s.height / 2 - gridRect.top;
          const tx = t.left - gridRect.left;
          const ty = t.top + t.height / 2 - gridRect.top;
          const midX = sx + (tx - sx) / 2;
          paths.push({ id: `sk-${c}-${r}`, d: `M ${sx} ${sy} H ${midX} V ${ty} H ${tx}` });
        }
      }
      this.connectorPaths = paths;
      this.connectorBox = { w: grid.scrollWidth, h: grid.scrollHeight };
    },
  },
};
</script>

<style lang="scss" scoped>
.bracket-skeleton {
  display: flex;
  gap: 56px;
  padding: 12px 4px 24px;
  position: relative;
}

.skeleton-connectors {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 0;
  overflow: visible;
}

.skeleton-column {
  display: flex;
  flex-direction: column;
  width: 220px;
  min-width: 200px;
  position: relative;
  z-index: 1;
}

.skeleton-header {
  width: 110px;
  height: 22px;
  border-radius: 14px;
  margin-bottom: 12px;
  background: linear-gradient(90deg, #ececec 0%, #f5f5f5 50%, #ececec 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}

.skeleton-card {
  height: 92px;
  border-radius: 10px;
  margin-bottom: 16px;
  background: linear-gradient(90deg, #f3f3f3 0%, #fafafa 50%, #f3f3f3 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}

@keyframes shimmer {
  0%   { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

.theme--dark .bracket-skeleton {
  .skeleton-header,
  .skeleton-card {
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.04) 0%,
      rgba(255, 255, 255, 0.08) 50%,
      rgba(255, 255, 255, 0.04) 100%
    );
    background-size: 200% 100%;
  }
}
</style>
